import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../stores/auth';

interface AccountUser {
  id: string;
  email: string;
  fullName: string;
  role: 'customer' | 'operator';
  status: 'active' | 'suspended';
  emailVerified: boolean;
}

export function AccountSettingsPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [user, setUser] = useState<AccountUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ user: AccountUser }>('/account')
      .then((data) => {
        if (cancelled) return;
        setUser(data.user);
        setFullName(data.user.fullName ?? '');
        setEmail(data.user.email ?? '');
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setLoadError(err.message || 'Could not load account.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleProfileSubmit(event: FormEvent) {
    event.preventDefault();
    setProfileSaving(true);
    setProfileMessage(null);
    try {
      const data = await api.patch<{ user: AccountUser }>('/account', { fullName, email });
      setUser(data.user);
      setProfileMessage('Account details saved.');
    } catch (err) {
      setProfileMessage((err as Error).message || 'Could not save account details.');
    } finally {
      setProfileSaving(false);
    }
  }

  async function handlePasswordSubmit(event: FormEvent) {
    event.preventDefault();
    setPasswordSaving(true);
    setPasswordMessage(null);
    try {
      await api.post('/account/password', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setPasswordMessage('Password updated.');
    } catch (err) {
      setPasswordMessage((err as Error).message || 'Could not update password.');
    } finally {
      setPasswordSaving(false);
    }
  }

  async function handleDeleteSubmit(event: FormEvent) {
    event.preventDefault();
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.post('/account/delete', { password: deletePassword });
      await logout();
      navigate('/');
    } catch (err) {
      setDeleteError((err as Error).message || 'Could not delete account.');
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="dash-page">
        <p className="dash-page-subtitle">Loading account…</p>
      </div>
    );
  }

  if (loadError || !user) {
    return (
      <div className="dash-page">
        <div className="sarnia-card" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>
          {loadError || 'Account unavailable.'}
        </div>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <div className="dash-page-header">
        <h1>Account settings</h1>
      </div>
      <div className="dash-settings-grid">
        <section className="sarnia-card dash-settings-card">
          <h2>Profile</h2>
          <p>Manage the name and email on your account.</p>
          <form onSubmit={handleProfileSubmit}>
            <div className="dash-field">
              <label htmlFor="fullName">Full name</label>
              <input id="fullName" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
            </div>
            <div className="dash-field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              {user.emailVerified ? null : <p className="dash-page-subtitle">Changing your email will require verification again.</p>}
            </div>
            {profileMessage && (
              <div className="sarnia-card" style={{ color: profileMessage.startsWith('Account') ? 'var(--success)' : 'var(--danger)' }}>
                {profileMessage}
              </div>
            )}
            <button type="submit" className="sarnia-btn sarnia-btn--primary" disabled={profileSaving}>
              {profileSaving ? 'Saving…' : 'Save profile'}
            </button>
          </form>
        </section>

        <section className="sarnia-card dash-settings-card">
          <h2>Password</h2>
          <p>Update the password you use to sign in.</p>
          <form onSubmit={handlePasswordSubmit}>
            <div className="dash-field">
              <label htmlFor="currentPassword">Current password</label>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="dash-field">
              <label htmlFor="newPassword">New password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
            {passwordMessage && (
              <div className="sarnia-card" style={{ color: passwordMessage.startsWith('Password') ? 'var(--success)' : 'var(--danger)' }}>
                {passwordMessage}
              </div>
            )}
            <button type="submit" className="sarnia-btn sarnia-btn--primary" disabled={passwordSaving}>
              {passwordSaving ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </section>

        <section className="sarnia-card dash-settings-card dash-settings-card--danger">
          <h2>Delete account</h2>
          <p>This permanently removes your account, sites, published pages and uploads. This cannot be undone.</p>
          <form onSubmit={handleDeleteSubmit}>
            <div className="dash-field">
              <label htmlFor="deletePassword">Confirm with your password</label>
              <input
                id="deletePassword"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                required
              />
            </div>
            {deleteError && (
              <div className="sarnia-card" style={{ color: 'var(--danger)' }}>
                {deleteError}
              </div>
            )}
            <button type="submit" className="sarnia-btn sarnia-btn--danger" disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete account'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
